import { DateTimeFormatter, LocalDate, LocalDateTime, LocalTime } from 'js-joda';

import { map } from 'es-toolkit/compat';
import AppSettings from 'settings/AppSettings';

/**
 * Defines a time range for a day which starts at 4am and ends at 4am the next day.
 * Stores min/max values from midnight to midnight and adjusts by four hours when
 * queried to allow the code to use LocalDateTime's concept of isBefore and isAfter when
 * all times are normalized to a single day in the past.
 */
export default class DayTimeRange {
  static aDay = LocalDate.parse('2015-01-01');
  static defaultMinTime = DayTimeRange.aDay.atTime(LocalTime.parse('00:00'));
  static defaultMaxTime = DayTimeRange.aDay.atTime(LocalTime.parse('23:59'));
  static timeFormatter = DateTimeFormatter.ofPattern('HH:mm');

  /**
   * The actual way to create an empty, range.
   */
  static create(): DayTimeRange {
    return new DayTimeRange();
  }

  useDefaults = true;
  minTime: LocalDateTime;
  maxTime: LocalDateTime;

  /**
   * Adjusts the time range to include all times between startTime and endTime.
   */
  union(startTime: string | LocalTime, endTime: string | LocalTime) {
    const minTime = this.toComparableStartTime(startTime);
    const maxTime = this.toComparableEndTime(endTime, minTime);
    if (this.useDefaults) {
      this.minTime = minTime;
      this.maxTime = maxTime;
    } else {
      if (minTime.isBefore(this.minTime)) {
        this.minTime = minTime;
      }
      if (maxTime.isAfter(this.maxTime)) {
        this.maxTime = maxTime;
      }
    }
    this.useDefaults = false;
  }

  /**
   * @returns {String} the mininum time in the range.
   */
  getMinTime(): string {
    return this.toString(this.getMinLocalDateTime());
  }

  /**
   * @returns {String} the maximum time in the range, which can be after midnight.
   */
  getMaxTime(): string {
    return this.toString(this.getMaxLocalDateTime());
  }

  /**
   * @returns {bool} whether the maximum time ends after midnight.
   */
  endsAfterMidnight(): boolean {
    return this.getMaxTime() <= AppSettings.LAST_TIME_IN_BUSINESS_DAY;
  }

  /**
   * Generator for retreiving times within the time range as String values.
   * @param numMinutes the size of the time increments in minutes.
   */
  getIncrements(numMinutes = 15): string[] {
    return map(this.getIncrementsAsLocalTimes(numMinutes), (time) =>
      time.format(DayTimeRange.timeFormatter)
    );
  }

  getIncrementsAsLocalTimes(numMinutes = 15): LocalTime[] {
    let returnedTime = this.getMinLocalDateTime();
    const maxTime = this.getMaxLocalDateTime();
    const increments: LocalTime[] = [];
    while (!returnedTime.isAfter(maxTime)) {
      increments.push(this.toLocalTime(returnedTime));
      returnedTime = returnedTime.plusMinutes(numMinutes);
    }
    return increments;
  }

  /** Returns increments within the time range as the number of minutes elapsed from the start time. */
  getMinuteIncrements(numMinutes = 15): number[] {
    const numberIncrements = this.getIncrementsAsLocalTimes(numMinutes).length;
    let minutesElapsed = 0;
    const increments: number[] = [];

    for (let i = 0; i < numberIncrements; i++) {
      increments.push(minutesElapsed);
      minutesElapsed += numMinutes;
    }

    return increments;
  }

  /**
   * @returns {Object} the minimum time in the range, as a LocalDateTime object.
   * @private
   */
  getMinLocalDateTime(): LocalDateTime {
    return this.useDefaults ? DayTimeRange.defaultMinTime : this.minTime;
  }

  /**
   * @returns {Object} the maximum time in the range, as a LocalDateTime object.
   * @private
   */
  getMaxLocalDateTime(): LocalDateTime {
    return this.useDefaults ? DayTimeRange.defaultMaxTime : this.maxTime;
  }

  /**
   * @returns {Object} the minimum time in the range, as a LocalTime object.
   * @private
   */
  getMinLocalTime(): LocalTime {
    return this.toLocalTime(this.getMinLocalDateTime());
  }

  /**
   * @returns {Object} the maximum time in the range, as a LocalTime object.
   * @private
   */
  getMaxLocalTime(): LocalTime {
    return this.toLocalTime(this.getMaxLocalDateTime());
  }

  /**
   * Converts a time into a LocalDateTime object that can be used for comparing against
   * other values in the range.
   * @param {String | LocalTime} time a time as a LocalTime or a string in 'HH:mm' format
   * @returns {Object} a LocalDateTime object that can be compared using just the hour/minute time value.
   * @private
   */
  toComparableStartTime(time: string | LocalTime): LocalDateTime {
    const localTime = typeof time === 'string' ? LocalTime.parse(time) : time;
    return DayTimeRange.aDay.atTime(localTime.minusHours(4));
  }

  /**
   * Converts a time into a LocalDateTime object that can be used for comparing against
   * other values in the range, taking into account that if the time is on the day cutoff, the
   * returned DateTime should be on the next day.
   * @param {String | LocalTime} time a time as a LocalTime or a string in 'HH:mm' format
   * @returns {Object} a LocalDateTime object that can be compared using just the hour/minute time value.
   * @private
   */
  toComparableEndTime(time: string | LocalTime, startDateTime: LocalDateTime): LocalDateTime {
    const dateTime = this.toComparableStartTime(time);
    const startTimeBetweenMidnightAndFour =
      !startDateTime.isBefore(
        DayTimeRange.aDay.atTime(DayTimeRange.defaultMinTime.toLocalTime())
      ) &&
      startDateTime.isBefore(
        DayTimeRange.aDay.atTime(LocalTime.parse(AppSettings.LAST_TIME_IN_BUSINESS_DAY))
      );
    const endtimeBetweenMidnightAndFour =
      !dateTime.isBefore(DayTimeRange.aDay.atTime(DayTimeRange.defaultMinTime.toLocalTime())) &&
      dateTime.isBefore(
        DayTimeRange.aDay.atTime(LocalTime.parse(AppSettings.LAST_TIME_IN_BUSINESS_DAY))
      );
    return endtimeBetweenMidnightAndFour && !startTimeBetweenMidnightAndFour
      ? dateTime.plusDays(1)
      : dateTime;
  }

  /**
   * Converts a LocalDateTime object into the relevant LocalTime
   * @private
   */
  toLocalTime(comparableLocalTime: LocalDateTime): LocalTime {
    return comparableLocalTime.plusHours(4).toLocalTime();
  }

  /**
   * Converts a LocalDateTime object back into a String in HH:mm format.
   * @private
   */
  toString(comparableLocalTime: LocalDateTime): string {
    return this.toLocalTime(comparableLocalTime).format(DayTimeRange.timeFormatter);
  }
}
